<template>
    <Dialog v-model:open="open">
        <DialogContent class="sm:max-w-[425px]">
            <DialogHeader>
                <DialogTitle>Edit link</DialogTitle>
            </DialogHeader>
            <form @submit="onSubmit">
                <div class="space-y-4">
                    <FormField name="title" v-slot="{ componentField }">
                        <FormItem>
                            <FormLabel>Title (Required)</FormLabel>
                            <FormControl>
                                <Text
                                    v-bind="componentField"
                                    placeholder="Title"
                                    :loading="loading"
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    </FormField>
                    <FormField name="description" v-slot="{ componentField }">
                        <FormItem>
                            <FormLabel>Description (Required)</FormLabel>
                            <FormControl>
                                <Text
                                    v-bind="componentField"
                                    placeholder="Description"
                                    :loading="loading"
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    </FormField>
                    <FormField name="url" v-slot="{ componentField }">
                        <FormItem>
                            <FormLabel>URL (Required)</FormLabel>
                            <FormControl>
                                <Url v-bind="componentField" placeholder="URL" :loading="loading" />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    </FormField>
                </div>
                <DialogFooter>
                    <Button type="button" variant="secondary" :disabled="saving" @click="close"
                        >Cancel</Button
                    >
                    <Button
                        type="submit"
                        :loading="saving"
                        :disabled="meta.valid === false || meta.dirty === false"
                        >Save</Button
                    >
                </DialogFooter>
            </form>
        </DialogContent>
    </Dialog>
</template>

<script setup lang="ts">
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import Text from "@/components/ui/input/Text.vue";
import Url from "@/components/ui/input/Url.vue";
import { defaultGraphQLClient } from "@/composables/default-urql-client";
import { toTypedSchema } from "@vee-validate/zod";
import gql from "graphql-tag";
import { useForm } from "vee-validate";
import { ref, watch } from "vue";
import { toast } from "vue-sonner";
import * as z from "zod";

const open = defineModel();

const props = defineProps<{
    uuid: string | null;
}>();

const emit = defineEmits(["success"]);
const saving = ref(false);
const loading = ref(false);

const schema = z.object({
    title: z.string().min(1, {
        message: "Title is required.",
    }),
    description: z.string().min(1, {
        message: "Description is required.",
    }),
    url: z
        .string()
        .min(1, {
            message: "Url is required.",
        })
        .url({
            message: "Url must be a valid URL.",
        }),
});

type Form = z.infer<typeof schema>;

const { handleSubmit, meta, resetForm, setFieldValue } = useForm({
    validationSchema: toTypedSchema(schema),
});

const onSubmit = handleSubmit((values) => {
    save(values);
});

watch(open, (value) => {
    if (value) {
        init();
    }
});

async function init() {
    try {
        loading.value = true;

        const { data, error } = await defaultGraphQLClient.query(
            gql`
                query getLink($uuid: String!) {
                    link(uuid: $uuid) {
                        uuid
                        title
                        description
                        url
                    }
                }
            `,
            {
                uuid: props.uuid,
            }
        );

        if (!error) {
            resetForm({
                values: {
                    title: data.link.title,
                    description: data.link.description,
                    url: data.link.url,
                },
            });
        }
    } finally {
        loading.value = false;
    }
}

function close() {
    open.value = false;
}

async function save(values: Form) {
    saving.value = true;

    try {
        const { error } = await defaultGraphQLClient.mutation(
            gql`
                mutation updateLink(
                    $uuid: String!
                    $title: String!
                    $description: String!
                    $url: String!
                ) {
                    updateLink(uuid: $uuid, title: $title, description: $description, url: $url) {
                        uuid
                    }
                }
            `,
            {
                uuid: props.uuid,
                title: values.title,
                description: values.description,
                url: values.url,
            }
        );

        if (!error) {
            toast.success("Success", {
                description: `Link was successfully updated.`,
            });

            emit("success");
            close();
        }
    } finally {
        saving.value = false;
    }
}
</script>
